import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import {
  FiClock,
  FiUser,
  FiFileText,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiActivity,
  FiTrendingUp,
  FiCalendar,
} from 'react-icons/fi';
import {
  useActivityLogs,
  useActivityStats,
  ActivityLog,
  ActivityLogFilters,
} from '../../hooks/useActivityLogs';

const ActivitiesPage: React.FC = () => {
  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActionType, setFilterActionType] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Use hooks
  const {
    logs,
    pagination,
    loading,
    error,
    applyFilters,
    goToPage,
    refresh,
    clearFilters,
  } = useActivityLogs({
    page: currentPage,
    pageSize: 20,
    autoFetch: true,
  });

  const { stats, loading: statsLoading } = useActivityStats();

  // Apply filters when they change
  const handleApplyFilters = () => {
    const filters: ActivityLogFilters = {};

    if (searchTerm) filters.search = searchTerm;
    if (filterActionType) filters.action_type = filterActionType;
    if (filterEntityType) filters.entity_type = filterEntityType;

    setCurrentPage(1);
    applyFilters(filters);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterActionType('');
    setFilterEntityType('');
    setCurrentPage(1);
    clearFilters();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    goToPage(page);
  };

  // Auto apply filters on search term change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleApplyFilters();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getActivityIcon = (actionType: string) => {
    if (actionType.includes('create')) return '🆕';
    if (actionType.includes('update')) return '✏️';
    if (actionType.includes('delete')) return '🗑️';
    if (actionType.includes('approve')) return '✅';
    if (actionType.includes('reject')) return '❌';
    if (actionType.includes('submit')) return '📤';
    return '📝';
  };

  const getActivityColor = (actionType: string) => {
    if (actionType.includes('create') || actionType.includes('approve'))
      return 'text-green-600 bg-green-50';
    if (actionType.includes('update')) return 'text-blue-600 bg-blue-50';
    if (actionType.includes('delete') || actionType.includes('reject'))
      return 'text-red-600 bg-red-50';
    if (actionType.includes('submit')) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) return 'เมื่อสักครู่';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} นาทีที่แล้ว`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ชั่วโมงที่แล้ว`;
    return `${Math.floor(diffInSeconds / 86400)} วันที่แล้ว`;
  };

  const getUserDisplayName = (log: ActivityLog) => {
    if (!log.user) return 'ระบบ';
    return `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() || log.user.email;
  };

  return (
    <AdminLayout title="กิจกรรมทั้งหมด">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">กิจกรรมทั้งหมดในระบบ</h1>
              <p className="text-gray-600">ติดตามการเปลี่ยนแปลงและกิจกรรมต่างๆ ในระบบ</p>
            </div>
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              รีเฟรช
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">วันนี้</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.today}</p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <FiCalendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">สัปดาห์นี้</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.this_week}</p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <FiActivity className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">เดือนนี้</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.this_month}</p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <FiTrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="ค้นหากิจกรรม..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter by Action Type */}
            <div className="relative">
              <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterActionType}
                onChange={(e) => {
                  setFilterActionType(e.target.value);
                  setTimeout(handleApplyFilters, 100);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="">ทุก Action Type</option>
                <option value="create_company">สร้างบริษัท</option>
                <option value="update_company">แก้ไขบริษัท</option>
                <option value="delete_company">ลบบริษัท</option>
                <option value="create_user">สร้างผู้ใช้</option>
                <option value="update_user">แก้ไขผู้ใช้</option>
                <option value="approve_claim">อนุมัติ Claim</option>
                <option value="reject_claim">ปฏิเสธ Claim</option>
                <option value="submit_change_request">ส่ง Change Request</option>
                <option value="approve_change_request">อนุมัติ Change Request</option>
                <option value="reject_change_request">ปฏิเสธ Change Request</option>
              </select>
            </div>

            {/* Filter by Entity Type */}
            <div className="relative">
              <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterEntityType}
                onChange={(e) => {
                  setFilterEntityType(e.target.value);
                  setTimeout(handleApplyFilters, 100);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="">ทุก Entity Type</option>
                <option value="company">บริษัท</option>
                <option value="user">ผู้ใช้</option>
                <option value="claim_request">Claim Request</option>
                <option value="change_request">Change Request</option>
                <option value="notification">Notification</option>
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || filterActionType || filterEntityType) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleClearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                ล้างตัวกรอง
              </button>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">เกิดข้อผิดพลาด: {error}</p>
          </div>
        )}

        {/* Activities List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">กำลังโหลดกิจกรรม...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <FiFileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">ไม่พบกิจกรรมในระบบ</p>
              <p className="text-sm text-gray-500">
                {searchTerm || filterActionType || filterEntityType
                  ? 'ลองเปลี่ยนตัวกรองหรือคำค้นหา'
                  : 'กิจกรรมจะแสดงที่นี่เมื่อมีการดำเนินการในระบบ'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {logs.map((log) => (
                <div key={log.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl ${getActivityColor(
                        log.action_type
                      )}`}
                    >
                      {getActivityIcon(log.action_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {log.description || log.action_type}
                      </p>

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <FiUser className="w-4 h-4" />
                          <span>{getUserDisplayName(log)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <FiClock className="w-4 h-4" />
                          <span>{formatTimeAgo(log.created_at)}</span>
                        </span>
                      </div>

                      {/* Metadata badges */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {log.action_type && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                            Action: {log.action_type}
                          </span>
                        )}
                        {log.entity_type && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                            Entity: {log.entity_type}
                          </span>
                        )}
                        {log.entity_id && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">
                            ID: {log.entity_id.substring(0, 8)}...
                          </span>
                        )}
                        {log.ip_address && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                            IP: {log.ip_address}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Timestamp */}
                    <div className="flex-shrink-0 text-xs text-gray-400">
                      {new Date(log.created_at).toLocaleString('th-TH', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                แสดง {logs.length} กิจกรรม จากทั้งหมด {pagination.total} กิจกรรม
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.has_prev}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>

                <span className="px-4 py-2 text-sm text-gray-700">
                  หน้า {pagination.page} จาก {pagination.total_pages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.has_next}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Most Active Users (ถ้ามี stats) */}
        {!statsLoading && stats && stats.most_active_users.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ผู้ใช้ที่ทำกิจกรรมมากที่สุด</h3>
            <div className="space-y-3">
              {stats.most_active_users.slice(0, 5).map((user, index) => (
                <div key={user.user_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">
                    {user.activity_count} กิจกรรม
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ActivitiesPage;
